import { formatDate } from '@cascade/core/utils';
import { useI18n } from '@cascade/react/i18n';
import { useTerms } from '@cascade/react/terms';
import { useAuthentication } from '@cascade/react/authentication';
import { Button, Markdown, Text } from '@cascade/ui-library';
import { useNavigate } from 'react-router-dom';
import classes from './Terms.module.scss';
import { SupportContact } from '#components/support-contact/index.ts';
import { HOME_PATH } from '#config/index.ts';
import { Layout } from '#layout/index.ts';
import type { TSignInLocationState } from '#views/sign-in/index.ts';

export const Terms = () => {
  const { locale, translate, translateError } = useI18n();
  const {
    termsText,
    termsUpdatedAt,
    isLoadingTerms,
    termsError,
    isAcceptingTerms,
    isAcceptanceRecorded,
    acceptTermsError,
    acceptTerms,
    reloadTerms,
  } = useTerms();

  const { signOut } = useAuthentication();
  const navigate = useNavigate();

  const isUnrecorded = isAcceptanceRecorded === false;
  const showActions = Boolean(termsText) || Boolean(termsError);
  const missingMessage = termsError
    ? translateError(termsError)
    : translate('terms.missing');

  const failureMessage = acceptTermsError
    ? translateError(acceptTermsError)
    : undefined;

  const unrecordedMessage = isUnrecorded
    ? translate('terms.notRecorded')
    : undefined;

  const acceptAlertMessage = failureMessage ?? unrecordedMessage;

  const updatedDate = termsUpdatedAt
    ? formatDate(termsUpdatedAt, { locale, dateStyle: 'long' })
    : undefined;

  const updatedMessage = updatedDate
    ? translate('terms.updated', { date: updatedDate })
    : undefined;

  const accept = () => {
    acceptTerms().catch(() => undefined);
  };

  const decline = () => {
    const state: TSignInLocationState = { termsDeclined: true };

    navigate(HOME_PATH, { replace: true, state });
    signOut();
  };

  return (
    <Layout
      mode='card'
      width='wide'
      title={translate('terms.title')}
      subtitle={updatedMessage}
      isLoading={isLoadingTerms}
    >
      <div className={classes.terms}>
        {termsText ? (
          <Markdown content={termsText} className={classes.text} />
        ) : (
          <>
            <Text type='body-2' role='alert' content={missingMessage} />
            <SupportContact />
          </>
        )}
        {acceptAlertMessage ? (
          <Text
            type='body-2'
            color='text-danger'
            role='alert'
            content={acceptAlertMessage}
          />
        ) : null}
        {showActions ? (
          <div className={classes.actions}>
            {termsText ? (
              <>
                <Button
                  variant='secondary'
                  label={translate('terms.decline')}
                  disabled={isAcceptingTerms}
                  onClick={decline}
                />
                <Button
                  label={translate('terms.accept')}
                  disabled={isAcceptingTerms}
                  onClick={accept}
                />
              </>
            ) : null}
            {termsError ? (
              <Button label={translate('terms.retry')} onClick={reloadTerms} />
            ) : null}
          </div>
        ) : null}
      </div>
    </Layout>
  );
};
